/**
 * SignInErrorModal Component
 * 
 * Modal for displaying sign-in error messages.
 * Supports dark/light themes and keyboard navigation.
 */

import { useEffect } from 'react';

/**
 * Close icon (X)
 */
const CloseIcon = () => (
  <svg
    className="cursor-pointer mt-1"
    width="12"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.00078 8.40078L2.10078 13.3008C1.91745 13.4841 1.68411 13.5758 1.40078 13.5758C1.11745 13.5758 0.884115 13.4841 0.700781 13.3008C0.517448 13.1174 0.425781 12.8841 0.425781 12.6008C0.425781 12.3174 0.517448 12.0841 0.700781 11.9008L5.60078 7.00078L0.700781 2.10078C0.517448 1.91745 0.425781 1.68411 0.425781 1.40078C0.425781 1.11745 0.517448 0.884115 0.700781 0.700781C0.884115 0.517448 1.11745 0.425781 1.40078 0.425781C1.68411 0.425781 1.91745 0.517448 2.10078 0.700781L7.00078 5.60078L11.9008 0.700781C12.0841 0.517448 12.3174 0.425781 12.6008 0.425781C12.8841 0.425781 13.1174 0.517448 13.3008 0.700781C13.4841 0.884115 13.5758 1.11745 13.5758 1.40078C13.5758 1.68411 13.4841 1.91745 13.3008 2.10078L8.40078 7.00078L13.3008 11.9008C13.4841 12.0841 13.5758 12.3174 13.5758 12.6008C13.5758 12.8841 13.4841 13.1174 13.3008 13.3008C13.1174 13.4841 12.8841 13.5758 12.6008 13.5758C12.3174 13.5758 12.0841 13.4841 11.9008 13.3008L7.00078 8.40078Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * SignInErrorModal component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} [props.title] - Error title
 * @param {string} [props.message] - Error message
 * @param {string} [props.details] - Additional error details
 */
export function SignInErrorModal({
  isOpen,
  onClose,
  title = 'Sign In Failed',
  message = 'The team domain or password is incorrect',
  details = 'Check your team domain or password and try again',
}) {
  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div
        className="relative rounded-lg w-[40%] min-w-[300px] max-w-[500px] p-6 pt-4 pb-14"
        style={{
          backgroundColor: 'var(--color-card)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div className="flex justify-between">
          <h1
            className="text-base font-medium"
            style={{ color: 'var(--color-foreground)' }}
          >
            {title}
          </h1>

          <button
            onClick={onClose}
            aria-label="Close modal"
            style={{ color: 'var(--color-foreground)' }}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mt-4">
          <h2
            className="text-xs font-medium mb-2"
            style={{ color: 'var(--color-foreground)' }}
          >
            {message}
          </h2>

          <p
            className="text-xs font-normal mb-2"
            style={{ color: 'var(--color-foreground)' }}
          >
            {details}
          </p>

          <button
            className="absolute left-6 mt-2 px-4 py-2 text-white text-xs font-medium rounded-lg transition"
            style={{
              backgroundColor: 'var(--color-primary)',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-primary-hover)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-primary)';
            }}
            onClick={onClose}
          >
            Ok
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignInErrorModal;
